// Import react elements
import { useContext, useEffect, useState } from "react";

// Import strich api calls
import { 
    requestFulfilmentCost,
    sendForFulfilment,
} from "api/APIStrichCalls";

// Import context function
import { AuthContext } from "global/context/AuthContext";
import { UserContext } from "global/context/UserContext";

// Import hooks
import { useToast } from "global/hooks/useToast";

// Import components
import CreateButton from "global/components/CreateButton";
import LoadingScreen from "global/components/loading_ui/LoadingScreen";
import FulfilmentCostBreakdownTable from "./FulfilmentCostBreakdownTable";

// Import utility functions
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";
import LoadingDotsAnimation from "global/components/loading_ui/LoadingDotsAnimation";
import { GridItem } from "global/components/Grid";
import { CloseIcon } from "global/strich_icons/StrichIcons";



const RequestFulfilmentPopUp = ({ 
    isOpen, 
    onClose, 
    orderDetails,
    serviceCodes, 
    setRequestFulfilmentPopupOpen, 
    fetchOrders, 
    fulfilmentRequestCosts, 
    setFulfilmentRequestCosts,  
}) => {

    //console.log("orderDetails", orderDetails);

    // Get the authentication token from the AuthContext (determines whether the user is logged in)
    const { token } = useContext(AuthContext);

    // Get user data from context
    const { userData } = useContext(UserContext);

    // Define toast options
    const { toastError, toastSuccess } = useToast();

    // Set state for storing the selected service code
    const [selectedServiceCode, setSelectedServiceCode] = useState(serviceCodes[0]?.service_code);

    // Loading state for sending fulfilment
    const [loadingSendingFulfilment, setLoadingSendingFulfilment] = useState(false);



    // Ensure the selected service code is filled when the serviceCodes update
    useEffect(() => {

        setSelectedServiceCode(serviceCodes[0]?.service_code);

    },[serviceCodes]);

    

    // Handle 'send for fulfilment'
    const handleSendForFulfilment = async (payload) => {

        // Set laoding state for sending fulfilment
        setLoadingSendingFulfilment(true);

        try {

            // Make the api call
            const response = await sendForFulfilment(token, payload);

            // Check for success response
            if (response.success) {

                // Refresh the order
                fetchOrders(orderDetails.id);

                // Close the request fulfilment pop up
                setRequestFulfilmentPopupOpen(false);

                // Toast success
                toastSuccess({ title: "Success", message: "Your order has been sent for fulfilment." });
               
            } else {

                // Create error message
                const errorMsg = response.error_message ? response.error_message : "Oops...something went wrong."

                // Toast an error
                toastError({ title: "Error", message: errorMsg, autoCloseMs: 3000 });

            }

            // Clear loading state for sending fulfilment
            setLoadingSendingFulfilment(false);

        }

        catch (error) {

            // Clear loading state for sending fulfilment
            setLoadingSendingFulfilment(false);

            // Toast an error
            toastError({ title: "Error", message: error, autoCloseMs: 3000 });

        }

    }



    // Handle getting the fulfilment cost
    const handleGetFulfilmentCost = async (orderID) => {

        // Create and format the payload for the call
        const payload = {
            order_id: orderID,
            service_code: selectedServiceCode,
        }

        try {

            // Make the call
            const response = await requestFulfilmentCost(token, payload);

            // Check if response is successful
            if (response.success) {

                // It is

                // Display a confrimation dialog
                DisplayConfirmationDialog(

                    // Dialog message
                    "Order ID: " + orderID + "<br /><br/> This is going to cost " + response.results.total.inc_tax + " (incl. tax)<br /><br/> Are you sure you want to send it for fulfilment?",

                    // Confirm button title
                    "Yes",

                    // Cancel button title
                    "No",

                    // Returned response from the dialog box
                    function (is_confirmed) {

                        // Check if the response is TRUE
                        if (is_confirmed) {

                            // It is

                            // Send it for fulfilment
                            handleSendForFulfilment(payload);

                        }

                    }

                );

            } else {

                // Create error message
                const errorMsg = response.error_message ? response.error_message : "Oops...something went wrong."

                // Toast an error
                toastError({ title: "Error", message: errorMsg, autoCloseMs: 3000 });

            }
        }

        catch (error) {

            // Toast an error
            toastError({ title: "Error", message: error, autoCloseMs: 3000 });

        }

    };



    // Handle select dropdown shipping method
    const handleServiceCodeChange = async (e) => {

        // Update in the selected code
        setSelectedServiceCode(e.target.value);

        // Create and format the payload for the call
        const payload = {
            order_id: orderDetails.id,
            service_code: e.target.value,
        }

        // Make the call
        const response = await requestFulfilmentCost(token, payload);

        // Check if response is successful
        if (response.success) {

            // Update the state
            setFulfilmentRequestCosts(response.results);

        } else {

            // Create error message
            const errorMsg = `Could not fetch fulfilment costs: ${response.error_message ? response.error_message  : "Oops something went wrong."}`;

            // Toast fail
            toastError({title: "Error", message: errorMsg, autoCloseMs: 3000});
        }

    };


    
    // If isOpen is false return early (don't show the pop up)
    if (!isOpen) return null;



    // Else show the pop up
    return (

        <>

            <div className="popup-overlay">

                <div className="popup-container popup-container--medium">

                    {/* This is the modal header */}
                    <div className="popup__header">

                        {/* This is the title area */}
                        <div className="popup__headerLeft">

                            {/* This is the parent title */}
                            <h2 className="popup__title">

                                Request Fulfilment

                            </h2>

                            {/* This is the parent subtitle */}
                            <p className="popup__subtitle">

                            </p>

                        </div>

                        {/* This is the header actions */}
                        <div className="popup__headerRight">

                            {/* This is the close button */}
                            <button type="button" className="popup__close" onClick={() => onClose?.()} title="Close">
                                <CloseIcon />
                            </button>

                        </div>

                    </div>  
                    
                    <br />

                    <div className="popup-content popup-content__body">

                        <GridItem>

                            <FulfilmentCostBreakdownTable costBreakdown={fulfilmentRequestCosts} showShippingSelector={true} serviceCodes={serviceCodes}  selectedServiceCode={selectedServiceCode} handleServiceCodeChange={handleServiceCodeChange} userData={userData} orderItemsFromOrderDetails={orderDetails?.orderitems} />

                        </GridItem>

                    </div>

                    {!serviceCodes ?
                            
                        <p>Loading Shipping Methods<LoadingDotsAnimation/></p>

                        :

                        <div className="tabs-footer tabs-footer--fill-no-background horizontal-align-content-center flex-justify-left-right">

                            <div>

                            </div>

                            <div className="horizontal-align-content-center">

                                {/* show the 'Fulfil Me' button, it should be disabled if no shipping service is selected or the shipping service selected is 'select' */}
                                <CreateButton title="Fulfil" size="l" onClick={() => handleGetFulfilmentCost(orderDetails.id)} disabled={!selectedServiceCode || selectedServiceCode === "select" || loadingSendingFulfilment} />

                                <CreateButton type="button" title="Cancel" isCancel={true} invert size="m" onClick={() => { onClose() }}/>

                                
                            </div>
                            
                        </div>

                    }

                    {/* Check if it's sending for fulfilment - can take a while - so show loading screen */}
                    {loadingSendingFulfilment &&

                        <LoadingScreen loadingMessage="Sending for fulfilment..." />
                    
                    }

                </div>

            </div>

        </>

    )

}

export default RequestFulfilmentPopUp;